import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Star, Download, Printer, ExternalLink, MessageSquareHeart } from "lucide-react";
import { useWizard } from "../lib/wizardStore";
import { Tip } from "./PublishAssistant";

const REVIEW_STATS = [
  { n: "98%", t: "of consumers read online reviews for local businesses.", s: "BrightLocal 2023" },
  { n: "88%", t: "trust online reviews as much as personal recommendations.", s: "BrightLocal" },
  { n: "~5×", t: "more clicks for profiles with 5+ reviews vs. zero reviews.", s: "Google" },
  { n: "1.7×", t: "more trustworthy when a business replies to reviews.", s: "Google" },
];

function StatCard({ n, t, s }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="font-heading text-3xl font-semibold bg-gradient-to-r from-blue-500 via-green-500 to-amber-400 bg-clip-text text-transparent">
        {n}
      </div>
      <p className="text-sm text-slate-700 mt-1 leading-relaxed">{t}</p>
      <div className="text-[11px] text-slate-400 mt-1.5">Source: {s}</div>
    </div>
  );
}

export function ReviewKit() {
  const { state, setReviewLink } = useWizard();
  const businessName = state.businessInfo.name || "our business";
  const link = state.reviewLink || "";
  const qrRef = useRef(null);
  const [flyerOpen, setFlyerOpen] = useState(false);

  const isValidUrl = (() => {
    try {
      if (!link) return false;
      new URL(link.startsWith("http") ? link : `https://${link}`);
      return true;
    } catch {
      return false;
    }
  })();

  const qrValue = isValidUrl ? (link.startsWith("http") ? link : `https://${link}`) : "";

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${businessName.replace(/\s+/g, "-").toLowerCase()}-google-review-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const printFlyer = () => {
    setFlyerOpen(true);
    setTimeout(() => window.print(), 250);
  };

  return (
    <div className="space-y-6" data-testid="review-kit">
      {/* Header */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquareHeart className="w-4 h-4 text-blue-600" />
          <h2 className="font-heading text-lg font-semibold">Your Review Kit</h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">
          A live Google profile is just the starting line. Reviews are what actually get you found — and get customers/visitors to choose you over the next result down the page. This kit gives you the tools to start collecting them from day one.
        </p>
      </div>

      {/* Why reviews matter */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
          <h3 className="font-heading text-base font-semibold">Why reviews are the difference</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {REVIEW_STATS.map((s) => <StatCard key={s.n} {...s} />)}
        </div>
        <p className="text-sm text-slate-600 mt-4 leading-relaxed">
          The takeaway: reviews decide whether people choose you or the next result down. The Google profile you just built gets you on the map — reviews are what get people to actually click <em>you</em>.
        </p>
        <Tip>
          Reply to every review — good, bad, or neutral. Google favors profiles that engage, and future customers/visitors read your replies just as carefully as the original review.
        </Tip>
      </div>

      {/* Paste Google review link */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading text-base font-semibold">Your Google review link</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Once Google verifies your profile, they give you a short review link (looks like <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">g.page/r/…/review</code>). Paste it here — the QR code and flyer below rebuild instantly.
        </p>

        <div className="mt-4">
          <label className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Paste your Google review URL</label>
          <input
            type="text"
            value={link}
            onChange={(e) => setReviewLink(e.target.value)}
            placeholder="https://g.page/r/xxxxxxxxxxxxx/review"
            data-testid="review-link-input"
            className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-400 focus:bg-white transition-colors"
          />
        </div>

        <details className="mt-4 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-slate-700">
          <summary className="cursor-pointer font-medium">How do I find my Google review link?</summary>
          <ol className="mt-3 list-decimal list-inside space-y-1.5 text-sm text-slate-600 leading-relaxed">
            <li>Open <a href="https://business.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">business.google.com <ExternalLink className="w-3 h-3" /></a> and sign in.</li>
            <li>Choose your verified profile.</li>
            <li>Look for the <strong>"Ask for reviews"</strong> button or the <strong>"Get more reviews"</strong> card on your dashboard.</li>
            <li>Google shows you a short link (starting with <code className="text-xs bg-white border border-slate-200 px-1 rounded">g.page/r/</code>). Copy it and paste it above.</li>
          </ol>
        </details>
      </div>

      {/* QR Code */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading text-base font-semibold">Your review QR code</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Print this and stick it where customers or visitors will see it — receipts, tables, entry doors, business cards, church bulletins, school newsletters. One scan drops them straight onto your Google review page.
        </p>

        <div className="mt-5 flex flex-col sm:flex-row gap-6 items-center sm:items-start">
          <div ref={qrRef} className="p-4 rounded-2xl border border-slate-200 bg-white">
            {isValidUrl ? (
              <QRCodeCanvas value={qrValue} size={200} level="H" includeMargin={false} data-testid="review-qr-canvas" />
            ) : (
              <div className="w-[200px] h-[200px] rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-center text-xs text-slate-400 px-4 leading-relaxed">
                Paste your Google review link above to generate a QR code
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="grid grid-cols-1 gap-2.5">
              <button
                type="button"
                onClick={downloadQR}
                disabled={!isValidUrl}
                data-testid="download-qr-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" /> Download QR (PNG)
              </button>
              <button
                type="button"
                onClick={printFlyer}
                disabled={!isValidUrl}
                data-testid="print-flyer-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white text-slate-800 px-5 py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Printer className="w-4 h-4" /> Print flyer with QR
              </button>
            </div>
            <Tip>
              Put the QR somewhere your happiest customers will see it right after a great experience — that's when they're most likely to leave a glowing review.
            </Tip>
          </div>
        </div>
      </div>

      {/* Print flyer (only visible when printing) */}
      <FlyerPrintable name={businessName} qrValue={qrValue} visible={flyerOpen && isValidUrl} onClose={() => setFlyerOpen(false)} />
    </div>
  );
}

function FlyerPrintable({ name, qrValue, visible, onClose }) {
  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-4 print:bg-white print:p-0 print:static">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-10 print:rounded-none print:shadow-none print:max-w-none print:w-full">
        <button
          type="button"
          onClick={onClose}
          className="print:hidden absolute top-3 right-4 text-slate-400 hover:text-slate-700 text-xl leading-none"
          aria-label="Close"
        >
          ×
        </button>
        <div className="text-center">
          <div className="inline-block rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Enjoyed your visit?
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold mt-4 leading-tight">
            Leave <span className="whitespace-nowrap">{name}</span> a Google review!
          </h1>
          <p className="text-slate-600 mt-3 text-sm leading-relaxed">
            Scan the code below with your phone camera — it takes 30 seconds and honestly means the world to a small team like ours.
          </p>
          <div className="mt-6 flex justify-center">
            <div className="p-5 rounded-2xl border-2 border-slate-900 bg-white">
              <QRCodeCanvas value={qrValue} size={220} level="H" includeMargin={false} />
            </div>
          </div>
          <div className="flex items-center justify-center gap-1 mt-6 text-amber-400">
            {[0,1,2,3,4].map((i) => <Star key={i} className="w-6 h-6 fill-amber-400" />)}
          </div>
          <p className="text-xs text-slate-400 mt-4">Thank you 💛</p>
        </div>
        <div className="print:hidden mt-6 flex gap-2 justify-center">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-slate-700"
          >
            <Printer className="w-4 h-4" /> Print now
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 bg-slate-100 text-slate-800 rounded-full px-5 py-2.5 text-sm font-medium hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
